/**
 * Order service
 * Handles order creation and shipping methods (auth via httpOnly session cookie)
 */
import APIClient from '../apiClient';

/**
 * Get available shipping methods.
 * Uses the httpOnly session cookie (withCredentials) for auth.
 */
export const getShippingMethods = async () => {
  const config = {
    headers: {
      Accept: 'application/json',
    },
  };
  const response = await APIClient.get('/orders/shipping-methods', config);
  return response.data;
};

/**
 * Create an order.
 * Payload: { customer?, items, shippingAddress, shippingMethodId }
 * Uses same auth behavior as rest of app (httpOnly session cookie via withCredentials).
 */
export const createOrder = async (payload) => {
  const config = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };
  const response = await APIClient.post('/orders', payload, config);
  return response.data;
};

/**
 * Initiate payment for an existing order.
 * Returns { paymentPageUrl } to redirect the user to the payment provider.
 */
export const initiatePayment = async (orderId) => {
  const config = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };
  const response = await APIClient.post(`/orders/${orderId}/pay`, {}, config);
  return response.data;
};

/**
 * Get a single order by its ID.
 * Returns the full order object including payment status.
 */
export const getOrderById = async (orderId) => {
  const config = {
    headers: {
      Accept: 'application/json',
    },
  };
  const response = await APIClient.get(`/orders/${orderId}`, config);
  return response.data;
};

/**
 * Download the invoice PDF for a paid order.
 * Triggers a browser download with the PDF file.
 * @param {string} orderId
 * @param {string} orderNumber - Used for the downloaded filename
 */
export const downloadInvoice = async (orderId, orderNumber) => {
  const response = await APIClient.get(`/orders/${orderId}/invoice`, {
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `invoice-${orderNumber || orderId}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Get all orders for the logged-in user.
 */
export const getOrders = async () => {
  const config = {
    headers: {
      Accept: 'application/json',
    },
  };
  const response = await APIClient.get('/orders', config);
  return response.data;
};
